import {
    type PriceCalculationUnit,
    type PriceCalculationValueWithUnit,
    type ParameterValueKind,
    type PriceCalculationUnitValueType,
    type ServiceDefinition,
    PriceCalculationParameterInfo,
} from "../api";
import { type BoundConstantValue } from "../api/parameter-values/ConstantValue";
import { ResolvedParameterValue } from "./ResolvedParameterValue";

/**
 * Represents a range value with minimum, maximum, step, and label.
 * Used for numeric parameters that have a range of possible values.
 */
export class ResolvedConstantValue extends ResolvedParameterValue<number, BoundConstantValue> {
    readonly unit?: PriceCalculationUnit;
    readonly calculationValue?: PriceCalculationUnitValueType;
    readonly productMin?: number;
    readonly productMax?: number;
    /**
     * Creates a new range value.
     *
     * @param value The range data containing min, max, step, and label bound to a parameter
     */
    constructor(value: Omit<BoundConstantValue, "kind">) {
        super({
            ...value,
            kind: "constant",
        });
        this.unit = value.unit;
        this.calculationValue = value.calculationValue;
        this.productMin = value.productMin;
        this.productMax = value.productMax;
    }

    getKind(): ParameterValueKind {
        return this.kind;
    }

    /**
     * Gets the unit for the calculation parameter.
     *
     * @returns The unit for the calculation parameter
     */
    getCalculationParameterParameterInfos(): PriceCalculationParameterInfo[] {
        if (this.unit === undefined) {
            return [];
        }
        return [
            {
                id: PriceCalculationParameterInfo.Id(this.parameterId.toString()),
                parameterId: this.parameterId,
                unit: this.unit,
                combineWithParameters: this.combineWithParameters,
                productMin: this.productMin,
                productMax: this.productMax,
            },
        ];
    }

    /**
     * Gets the value for the calculation parameter.
     *
     * @param value The value of the parameter
     * @returns The value for the calculation parameter
     */
    getCalculationParameterValues(_value: number | undefined): Record<string, PriceCalculationValueWithUnit> {
        if (this.unit === undefined || this.calculationValue === undefined) {
            return {};
        }
        return { [this.parameterId.toString()]: { value: this.calculationValue, unit: this.unit } };
    }

    /**
     * Gets the view information for the parameter.
     *
     * @param values The value of the parameter
     * @returns The view information for the parameter
     */
    getViewText(_values: number | undefined): string | undefined {
        return undefined;
    }

    /**
     * Creates a customer-safe version of this constant value.
     * Constant parameters are only for pricing calculations and should not be visible to customers.
     *
     * @param requestPricing - The pricing mode of the service definition
     * @returns For "show-price" and "show-disclosed-price": this instance
     *          For "show-no-price": undefined (constant should be filtered out)
     */
    toCustomerValue(requestPricing: ServiceDefinition.RequestPricing): this | undefined {
        if (requestPricing === "show-no-price") {
            return undefined;
        }
        return this;
    }

    /**
     * Creates a new range value with valid step and label.
     *
     * @param value The range data containing min, max, step, and label bound to a parameter
     * @returns A new range value or throws an error if the values are invalid
     */
    static create(value: Omit<BoundConstantValue, "kind">): ResolvedConstantValue | never {
        return new ResolvedConstantValue(value);
    }
}
